/**
 * Client bootstrap (DESIGN 7.3 main.ts, CROSS-PACKAGE CONTRACTS): the
 * canvas, ClientState, Socket, Renderer, Controls and Sfx are created
 * here, the DOM screens are mounted on their overlay roots, the Controls
 * intents (chat / spellbook / mute) are wired to the screens, and one
 * requestAnimationFrame loop runs the frame:
 *
 *   controls.update -> state.flushInput -> socket.send (20 Hz inside)
 *   -> state.frame -> renderer.draw -> sfx.onEvent per event
 *   -> sfx.onQueue on new queue feedback -> sfx.onFrame
 *
 * `?dev=1` renders the WP6 dev scene (client/src/render/devScene.ts)
 * without connecting; the module is loaded through import.meta.glob so a
 * missing file is a toast, not a broken client.
 */

import './styles.css';
import type { ViewModel } from './state/viewModel.js';
import { ClientState, emptyViewModel } from './state/clientState.js';
import { Socket } from './net/socket.js';
import { Renderer } from './render/renderer.js';
import { Controls } from './input/controls.js';
import { Sfx } from './audio/sfx.js';
import { type Screen, button, el, setButtonClickHook, toast } from './ui/dom.js';
import { mount as mountLanding } from './ui/landing.js';
import { mount as mountLobby } from './ui/lobby.js';
import { mount as mountChat } from './ui/chat.js';
import { mount as mountOnboarding } from './ui/onboarding.js';
import { mount as mountResults } from './ui/results.js';
import { mount as mountSpellbook } from './ui/spellbook.js';
import { mountPauseMenu } from './ui/pauseMenu.js';
import { mountWaitButton } from './ui/waitButton.js';
import { loadKeymap, shortLabel, type Keymap } from './input/keymap.js';
import { setElementKeyLabels, setHudTouchMode, setMagickKeyLabels } from './render/hudQueue.js';
import { setDepthKeyLabels } from './render/hudDepth.js';
import { elementKeyLabels, mountSettings } from './ui/settings.js';
import { mountSoundPopover } from './ui/soundPopover.js';
import { TouchControls, lockTouchPage, prefersTouch } from './input/touch.js';
import { tabMovesFocus } from './input/tabFocus.js';
import { mountTopBar } from './ui/topBar.js';
import { runFrameLoop } from './frameLoop.js';

/** A hidden tab stops rAF; cap the step so one frame never simulates seconds. */
const MAX_FRAME_DT = 0.1;
/** Minimum gap between "Wet!" hints (DESIGN 6.7), so a panicked A-mash shows one. */
const SHOCK_HINT_MS = 4000;

/** A frame threw: log it, and tell the player once per page so a stuck picture has an explanation. */
let frameErrorShown = false;
function reportFrameError(err: unknown): void {
  console.error('frame failed', err);
  if (frameErrorShown) return;
  frameErrorShown = true;
  toast('Something went wrong drawing the game. If it looks stuck, refresh the page.', 'error', 8000);
}

type DevSceneFrame = (tSeconds: number, dtSeconds: number) => ViewModel;

function byId(id: string): HTMLElement {
  const node = document.getElementById(id);
  if (!node) throw new Error(`index.html is missing #${id}`);
  return node;
}

/** The overlay root for a screen: the one from index.html, or a fresh one under #ui. */
function overlayRoot(ui: HTMLElement, id: string): HTMLElement {
  const existing = document.getElementById(id);
  if (existing) return existing;
  const node = el('div', { id, class: 'overlay' });
  node.hidden = true;
  ui.appendChild(node);
  return node;
}

function isViewModel(x: unknown): x is ViewModel {
  if (typeof x !== 'object' || x === null) return false;
  const vm = x as Partial<ViewModel>;
  return Array.isArray(vm.players) && Array.isArray(vm.events) && Array.isArray(vm.beams);
}

/**
 * The dev scene module, if WP6 shipped one. Accepts either shape:
 * `buildDevScene(t, dt): ViewModel` called every frame, or
 * `buildDevScene(): { frame(t, dt): ViewModel }` called once.
 */
async function loadDevScene(): Promise<DevSceneFrame | null> {
  const modules = import.meta.glob<Record<string, unknown>>('./render/devScene.ts');
  const loader = modules['./render/devScene.ts'];
  if (!loader) return null;
  const mod = await loader();
  const build = mod['buildDevScene'];
  if (typeof build !== 'function') return null;
  const fn = build as (t: number, dt: number) => unknown;
  const first = fn(0, 0);
  if (isViewModel(first)) {
    return (t, dt) => {
      const vm = fn(t, dt);
      return isViewModel(vm) ? vm : first;
    };
  }
  const scene = first as { frame?: unknown } | null;
  if (scene && typeof scene.frame === 'function') {
    const frame = scene.frame as (t: number, dt: number) => unknown;
    return (t, dt) => {
      const vm = frame.call(scene, t, dt);
      return isViewModel(vm) ? vm : emptyViewModel();
    };
  }
  return null;
}

function main(): void {
  mountTopBar();
  const canvas = byId('game') as HTMLCanvasElement;
  const ui = byId('ui');
  const dev = new URLSearchParams(window.location.search).get('dev') === '1';

  const state = new ClientState();
  const socket = new Socket();
  socket.onAny((m) => state.onServerMsg(m));
  const renderer = new Renderer(canvas);
  const controls = new Controls(canvas, state, (px, py) => renderer.screenToWorld(px, py));
  const sfx = new Sfx();
  // Saved control bindings (settings screen) drive both input and the HUD key caps.
  const applyKeymap = (map: Keymap): void => {
    controls.setKeymap(map);
    setElementKeyLabels(elementKeyLabels(map));
    setMagickKeyLabels(map.magick.map(shortLabel));
    setDepthKeyLabels({
      weave: shortLabel(map.weave[0] ?? map.weave[1]),
      stash: [shortLabel(map.stash1[0] ?? map.stash1[1]), shortLabel(map.stash2[0] ?? map.stash2[1])],
      magick: shortLabel(map.magick[0] ?? map.magick[1]),
    });
  };
  applyKeymap(loadKeymap(safeStorage()));

  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  renderer.setReduceMotion(motion.matches);
  motion.addEventListener('change', (ev) => renderer.setReduceMotion(ev.matches));

  const resize = (): void => renderer.resize();
  window.addEventListener('resize', resize);
  resize();

  // The glyph cut into the arena wall (ground.ts) names itself when the cursor rests on it: the
  // browser's own tooltip, so nothing is drawn and play is untouched.
  canvas.addEventListener('mousemove', (ev) => {
    const r = canvas.getBoundingClientRect();
    const hint = renderer.trailHintAt(ev.clientX - r.left, ev.clientY - r.top) ?? '';
    if (canvas.title !== hint) canvas.title = hint;
  });

  // Browsers only let audio start from a gesture; resume() is idempotent and also wakes a suspended context.
  // touchend is the one iOS counts; it also kicks off the sound manifest download.
  const gesture = (): void => sfx.resume();
  window.addEventListener('pointerdown', gesture);
  window.addEventListener('keydown', gesture);
  window.addEventListener('touchend', gesture, { passive: true });
  setButtonClickHook(() => sfx.ui('click'));

  if (dev) {
    runDevScene(renderer, controls, sfx);
    return;
  }

  const screens: Screen[] = [];
  const landing = mountLanding(overlayRoot(ui, 'landing'), state, socket);
  const lobby = mountLobby(overlayRoot(ui, 'lobby'), state, socket);
  const chat = mountChat(overlayRoot(ui, 'chat'), state, socket);
  const onboarding = mountOnboarding(overlayRoot(ui, 'onboarding'), state, socket);
  const results = mountResults(overlayRoot(ui, 'results'), state, socket);
  const spellbook = mountSpellbook(overlayRoot(ui, 'spellbook'), state, socket);
  let settingsOpen = false;
  const settings = mountSettings(overlayRoot(ui, 'settings'), {
    getKeymap: () => controls.keymap,
    setKeymap: applyKeymap,
    onOpenChange: (open) => { settingsOpen = open; syncControls(); },
  });
  const leaveRoom = (): void => {
    socket.send({ t: 'leave' });
    state.leaveRoom();
    window.history.replaceState(null, '', window.location.pathname);
  };
  // The menu (Esc / P / the pause button): alone in the room it pauses the match, with others it only opens.
  let menuOpen = false;
  const pauseMenu = mountPauseMenu(overlayRoot(ui, 'pausemenu'), state, {
    openSettings: () => settings.open(),
    openBook: () => spellbook.toggle(),
    leave: leaveRoom,
    isMenuKey: (code) => controls.keymap.pause.includes(code),
    onOpenChange: (open) => { menuOpen = open; syncControls(); },
  });
  // Ready for what comes next (N, or the button during a wait): skips it once everyone in the room has pressed.
  const sendNext = (): void => socket.send({ t: 'next' });
  const waitButton = mountWaitButton(overlayRoot(ui, 'waitbar'), state, {
    send: sendNext,
    resultsShown: () => results.shown,
    // no key hint on a touch screen: the button is the way
    keyLabel: () => (touch ? '' : shortLabel(controls.keymap.ready[0] ?? controls.keymap.ready[1])),
  });
  screens.push(landing, lobby, chat, onboarding, results, spellbook, settings, pauseMenu, waitButton);
  // In a room the gear is the menu's (pause) button, on phones too; outside one it opens the settings.
  const gear = button('⚙', () => {
    if (state.inRoom) pauseMenu.toggle();
    else if (settingsOpen) settings.close();
    else settings.open();
  }, 'btn gear-btn');
  ui.appendChild(gear);
  let gearInRoom: boolean | null = null;
  const syncGear = (): void => {
    if (gearInRoom === state.inRoom) return;
    gearInRoom = state.inRoom;
    gear.textContent = state.inRoom ? '❚❚' : '⚙';
    gear.title = state.inRoom ? 'Menu: pause, controls, leave (Esc)' : 'Settings and controls (O)';
    gear.setAttribute('aria-label', state.inRoom ? 'Menu and pause' : 'Settings');
  };
  // "Paused" over the arena while the server holds the match for a menu other than this one (the settings, the book).
  const pausedChip = el('div', { class: 'paused-chip', role: 'status' }, 'Paused');
  pausedChip.hidden = true;
  ui.appendChild(pausedChip);
  // Tell the server whether a menu is open (it decides whether that pauses: only for a room's only player). Sent
  // again now and then while it should be paused and isn't, e.g. after a reconnect.
  let pauseSent = false;
  let pauseSentAt = 0;
  const syncPause = (now: number): void => {
    const want = state.inRoom && (menuOpen || settingsOpen || spellbook.pinned);
    const solo = (state.room?.players.length ?? 0) === 1;
    const stale = want && solo && !state.paused && now - pauseSentAt > 1500;
    if (want !== pauseSent || stale) {
      socket.send({ t: 'pause', on: want });
      pauseSent = want;
      pauseSentAt = now;
    }
    pausedChip.hidden = !(state.paused && !menuOpen);
  };
  // The spellbook also opens from a button, so nobody has to discover that Tab is held.
  const bookBtn = button('Book', () => spellbook.toggle(), 'btn book-btn');
  bookBtn.title = 'Spellbook: every combo, Magick and status (hold Tab)';
  ui.appendChild(bookBtn);
  // Music and effects levels one click away, in the lobby and mid-match.
  const sound = mountSoundPopover(ui);

  // DESIGN 6.1: keys belong to the game only while in a room and the chat box is closed.
  let chatOpen = false;
  // Tab walks the menus outside a match and is the spellbook key during one (input/tabFocus.ts).
  const walkingMenus = (): boolean => tabMovesFocus({ inRoom: state.inRoom, phase: state.phase, menuOpen: spellbook.pinned });
  controls.setTabMovesFocus(walkingMenus);
  let wasWalkingMenus = true;
  const syncControls = (): void => {
    controls.setEnabled(state.inRoom && !chatOpen && !settingsOpen && !menuOpen);
    const walking = walkingMenus();
    if (walking === wasWalkingMenus) return;
    wasWalkingMenus = walking;
    // A match starts: no button may keep focus, or Space and Enter would press it instead of casting.
    const held = document.activeElement;
    if (!walking && !chatOpen && held instanceof HTMLElement && held.matches('button, a[href]')) held.blur();
  };
  chat.onOpenChange((open) => {
    chatOpen = open;
    syncControls();
  });
  // The cheat code (discoveries.ts): straight to the page it opened.
  chat.onReveal(() => spellbook.openOn('weaves'));
  controls.on('chat', (open) => { if (open) chat.open(); });
  controls.on('settings', () => settings.open());
  controls.on('pause', () => pauseMenu.open());
  controls.on('ready', () => { if (state.room?.wait) sendNext(); });
  controls.on('spellbook', (open) => (open ? spellbook.show() : spellbook.hide()));
  // Controls reports its own toggle; the Sfx holds the persisted truth, so M is treated as "flip".
  controls.on('mute', () => {
    sfx.setMuted(!sfx.muted);
    sound.refresh();
    toast(sfx.muted ? 'Sound off (M to unmute)' : 'Sound on', 'info', 1500);
  });
  // Touch devices: zoomed-out world, locked page, on-screen controls. A touchscreen laptop switches on first touch.
  let touch: TouchControls | null = null;
  const enableTouch = (): void => {
    if (touch) return;
    document.documentElement.classList.add('touch');
    renderer.setTouchMode(true);
    setHudTouchMode(true);
    lockTouchPage();
    touch = new TouchControls(canvas, controls, ui, () => spellbook.toggle());
  };
  if (prefersTouch()) enableTouch();
  window.addEventListener('pointerdown', (e) => { if (e.pointerType === 'touch') enableTouch(); }, { capture: true });

  window.addEventListener('pagehide', () => {
    // A refresh lands back in the same wizard if it reloads inside the reconnect window.
    state.rememberSession();
    for (const s of screens) s.dispose();
    controls.dispose();
  });
  // Back/forward cache: the page above was torn down on pagehide, so a restored copy has dead
  // screens and controls. Load it fresh instead (it resumes the session like a refresh would).
  window.addEventListener('pageshow', (e) => { if (e.persisted) window.location.reload(); });

  let last = performance.now();
  let lastFeedbackAt = -1;
  let lastShockHint = -SHOCK_HINT_MS;
  runFrameLoop((cb) => window.requestAnimationFrame(cb), (now: number): void => {
    const dt = Math.min(MAX_FRAME_DT, Math.max(0, (now - last) / 1000));
    last = now;
    syncControls();
    syncGear();
    syncPause(now);
    touch?.setVisible(state.inRoom && !chatOpen && !settingsOpen && !menuOpen);
    controls.update(dt);
    const input = state.flushInput(now);
    if (input) socket.send(input);
    const vm = state.frame(now);
    renderer.setArena(state.arena);
    renderer.draw(vm, dt);
    for (const ev of vm.events) sfx.onEvent(ev, vm);
    const fb = vm.me?.queueFeedback;
    if (fb && fb.at !== lastFeedbackAt) {
      lastFeedbackAt = fb.at;
      sfx.onQueue(fb.outcome, fb.el);
      if (fb.outcome === 'self_shock' && now - lastShockHint >= SHOCK_HINT_MS) {
        lastShockHint = now;
        toast('Wet! Lightning shocks you. Self-cast Fire (F, then middle click) to dry off.', 'error');
      }
    }
    sfx.onFrame(vm, dt);
  }, reportFrameError);
}

/** `?dev=1`: draw the renderer's dev scene with no server; events still reach the Sfx so sounds can be auditioned. */
function runDevScene(renderer: Renderer, controls: Controls, sfx: Sfx): void {
  let scene: DevSceneFrame | null = null;
  let ready = false;
  void loadDevScene().then((fn) => {
    scene = fn;
    ready = true;
    toast(fn ? 'Dev scene: no server connection' : 'No dev scene found (client/src/render/devScene.ts)', fn ? 'info' : 'error');
  }).catch((err: unknown) => {
    ready = true;
    toast(`Dev scene failed to load: ${err instanceof Error ? err.message : String(err)}`, 'error');
  });
  controls.setEnabled(false);
  const start = performance.now();
  let last = start;
  runFrameLoop((cb) => window.requestAnimationFrame(cb), (now: number): void => {
    const dt = Math.min(MAX_FRAME_DT, Math.max(0, (now - last) / 1000));
    last = now;
    controls.update(dt);
    const vm = ready && scene ? scene((now - start) / 1000, dt) : emptyViewModel();
    renderer.draw(vm, dt);
    for (const ev of vm.events) sfx.onEvent(ev, vm);
    sfx.onFrame(vm, dt);
  }, reportFrameError);
}

main();

function safeStorage(): Storage | null {
  try { return window.localStorage; } catch { return null; }
}
